import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { nb } from 'date-fns/locale/nb';
import { nn } from 'date-fns/locale/nn';
import { RefObject } from 'react';

import { format, getZonedDate, ZonedDate } from '@eika-infrastruktur/date';

import { CalendarConfig, CalendarDate, CalendarDateDetail, CalendarRow, Language } from '../types';

const datePattern = 'dd.MM.yyyy';

export const generateId = (prefix: string): string => {
    return `${prefix || 'calendar'}-${new Date().getTime()}-${Math.floor(Math.random() * 10000 + 1)}-${Math.floor(
        Math.random() * 10000 + 1,
    )}`;
};

export const isTimeZoneDifferent = (): boolean => {
    const pattern = 'dd.MM.yyyy hh:mm';
    const clientDate = new Date();
    const zonedDate = getZonedDate();
    const textZonedDate = format(zonedDate, pattern);
    const textClientDate = format(clientDate, pattern);
    return textZonedDate !== textClientDate;
};

export const getDateAsList = (date: ZonedDate = getZonedDate(), addMonth = false): number[] => {
    return [date.getFullYear(), date.getMonth() + (addMonth ? 1 : 0), date.getDate()];
};

export const clonedDateToZonedDate = (date?: Date): ZonedDate => {
    const cloned = date ? new Date(date.getTime()) : new Date();
    return getZonedDate(cloned);
};

export const convertDateToText = (date: ZonedDate = getZonedDate()): string => {
    return format(date, datePattern);
};

export const getTimezoneDifferentMessage = (language?: Language): string => {
    return language === 'nn'
        ? 'Vi forheld seg til tidssona i Noreg, viss du er i eit land i ei anna tidssone er det viktig å berekna tid etter dette.'
        : 'Vi forholder seg til tidssonen i Norge, hvis du er i et land i en annen tidssone er det viktig å beregne tid etter dette.';
};

export const getMonthNameList = (shortname?: boolean): string[] => {
    const date = getZonedDate();
    date.setDate(1);
    return Array.from({ length: 12 }).map((x, i) => {
        date.setMonth(i);
        const text = format(date, shortname ? 'MMM' : 'MMMM');
        return `${text.substring(0, 1).toUpperCase()}${text.substring(1)}`;
    });
};

export const getWeekNameList = (shortname?: boolean, language?: Language): string[] => {
    const date = getZonedDate();
    date.setDate(date.getDate() - date.getDay() - 1);

    return Array.from({ length: 7 }).map(() => {
        date.setDate(date.getDate() + 1);
        const text = format(date, 'EEEE', { locale: language === 'nn' ? nn : nb });
        return shortname
            ? text.substring(0, 1).toUpperCase()
            : `${text.substring(0, 1).toUpperCase()}${text.substring(1)}`;
    });
};

export const convertToStandardKeyboardCode = (key?: string): string | undefined => {
    switch (key) {
        case 'Esc':
            return 'Escape';
        case 'Spacebar':
            return ' ';
        case 'Left':
            return 'ArrowLeft';
        case 'Right':
            return 'ArrowRight';
        case 'Up':
            return 'ArrowUp';
        case 'Down':
            return 'ArrowDown';
        default:
            return key;
    }
};

export const compareDate = (firstDate?: ZonedDate, secondDate?: ZonedDate, same?: boolean): boolean => {
    if (!firstDate || !secondDate) {
        return false;
    }
    return same ? convertDateToText(firstDate) === convertDateToText(secondDate) : isAfter(secondDate, firstDate);
};

export const inSameMonth = (date1: ZonedDate, date2: ZonedDate): boolean =>
    date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();

export const firstInMonth = (date: ZonedDate): ZonedDate => {
    const copy = clonedDateToZonedDate(date);
    copy.setDate(1);
    return copy;
};

export const getPrimaryDateInView = (view: ZonedDate, selected?: ZonedDate): ZonedDate => {
    if (selected && inSameMonth(selected, view)) {
        return selected;
    }
    const today: ZonedDate = getZonedDate();
    if (inSameMonth(today, view)) {
        return today;
    }
    return firstInMonth(view);
};

export const getCalendarTableCaption = (date?: Date): string => {
    return date ? `Kalender ${date.getFullYear()}` : 'Kalender';
};

export const getCalendarDateTitle = (
    date: ZonedDate = getZonedDate(),
    testSameYear = false,
    monthShortname = false,
    alwaysShowYear = false,
): string => {
    const now = getZonedDate();
    const isSameYear = testSameYear ? date.getFullYear() === now.getFullYear() : false;
    const monthNameList = getMonthNameList(monthShortname || alwaysShowYear);
    const month = monthNameList[date.getMonth()];
    return isSameYear && !alwaysShowYear ? month : `${month} ${date.getFullYear()}`;
};

export const getCalendarDateAriaText = (
    date: ZonedDate = getZonedDate(),
    monthNameList: string[] = [],
    weekNameList: string[] = [],
): string => {
    const day = weekNameList[date.getDay()];
    const month = monthNameList[date.getMonth()].toLowerCase();
    return `${day} ${date.getDate()}. ${month} ${date.getFullYear()}`;
};

export const getCalendarDateDetail = (
    date: ZonedDate = getZonedDate(),
    monthNameList: string[] = [],
    weekNameList: string[] = [],
): CalendarDateDetail => {
    const day = weekNameList[date.getDay()];

    return {
        monthNameText: monthNameList[date.getMonth()],
        monthText: format(date, 'MM'),
        weekNameText: day,
        weekShortnameText: day.substring(0, 3),
        dateText: format(date, 'dd'),
        date: date.getDate(),
        year: date.getFullYear(),
    };
};

export const getCalendarConfig = (
    date: ZonedDate,
    focusRef: RefObject<HTMLButtonElement>,
    onPrimayFocus: () => void,
    select?: ZonedDate,
    minDate?: ZonedDate,
    maxDate?: ZonedDate,
    focus?: ZonedDate,
    focusable?: ZonedDate,
    language?: Language,
    alwaysShowYear = false,
): CalendarConfig => {
    const now = getZonedDate();
    const data = getDateAsList(date);
    const start = new ZonedDate(data[0], data[1], 1, 0, 0, 0, 0, 'Europe/Oslo');

    if (start.getDay()) {
        start.setDate(1 - (start.getDay() - 1));
    } else {
        start.setDate(1 - 6);
    }

    const monthNameList = getMonthNameList();
    const weekNameList = getWeekNameList(false, language);

    const row: CalendarRow[] = Array.from({ length: 6 }).map((x, i) => {
        const column: CalendarDate[] = Array.from({ length: 7 }).map((y, j) => {
            const copy = clonedDateToZonedDate(start);
            const count = j + i * 7;
            copy.setDate(copy.getDate() + count);
            return {
                id: `column-${j}-${copy.getTime()}`,
                aria: getCalendarDateAriaText(copy, monthNameList, weekNameList),
                text: `${copy.getDate()}`,
                zonedDate: copy,
                selected: compareDate(copy, select, true),
                disabled:
                    (!compareDate(copy, minDate, true) && compareDate(copy, minDate)) || compareDate(maxDate, copy),
                outOfMonth: copy.getMonth() !== date?.getMonth(),
                weekEnd: copy.getDay() === 0 || copy.getDay() === 6,
                today: compareDate(copy, now, true),
                focus: compareDate(copy, focus, true),
                detail: getCalendarDateDetail(copy, monthNameList, weekNameList),
                focusable: compareDate(copy, focusable, true),
                onFocus: compareDate(copy, focusable, true) ? onPrimayFocus : undefined,
                buttonref: compareDate(copy, focus, true) ? focusRef : undefined,
            };
        });
        return { id: `row-${start.getMonth()}-${i}`, list: column };
    });

    const previousDate = new Date(data[0], data[1], 0, 0, 0, 0, 0);
    const previous = getZonedDate(previousDate);
    const previousMonth =
        !minDate || compareDate(minDate, previous) || compareDate(minDate, previous, true)
            ? {
                  aria: `Vis forrige måned, ${getCalendarDateTitle(previous)}`,
                  zonedDate: previous,
              }
            : undefined;

    const nextDate = new Date(data[0], data[1] + 1, 1, date.getHours());
    const next = getZonedDate(nextDate);
    const lastWeekInRange = row[row.length - 1].list;
    const end = lastWeekInRange[lastWeekInRange.length - 1].zonedDate;
    const nextMonth =
        !maxDate || compareDate(next, maxDate) || compareDate(next, maxDate, true)
            ? {
                  aria: `Vis neste måned, ${getCalendarDateTitle(next)}`,
                  zonedDate: next,
              }
            : undefined;

    const weekShortnameList = getWeekNameList(true, language);
    weekNameList.push(weekNameList.shift() ?? '');
    weekShortnameList.push(weekShortnameList.shift() ?? '');

    return {
        stamp: date.getTime(),
        title: {
            aria: getCalendarDateTitle(date),
            text: getCalendarDateTitle(date, true, false, alwaysShowYear),
            fontLargeScale: getCalendarDateTitle(date, false, true, alwaysShowYear),
        },
        nextMonth,
        previousMonth,
        list: row,
        tableCaption: getCalendarTableCaption(date),
        tableHead: weekShortnameList.map((text, i) => {
            return { text, aria: weekNameList[i], id: `head-${date?.getMonth() ?? 'x'}-${i}` };
        }),
        dateSpan: [getZonedDate(start), getZonedDate(end)],
    };
};

const shiftByDays = (date: ZonedDate, shiftByNumberOfDays: number): ZonedDate => {
    const copy = new ZonedDate(date, undefined);
    copy.setDate(date.getDate() + shiftByNumberOfDays);
    return copy;
};

const clampDate = (date: ZonedDate, minDate?: ZonedDate, maxDate?: ZonedDate): ZonedDate => {
    if (minDate && isBefore(date, minDate)) {
        return minDate;
    }
    if (maxDate && isAfter(date, maxDate)) {
        return maxDate;
    }
    return date;
};

export const getNextFocusDateInTableByKeyboardArrow = (
    calendarList: CalendarRow[],
    currentView: ZonedDate,
    key: string,
    currentFocus?: ZonedDate,
    minDate?: ZonedDate,
    maxDate?: ZonedDate,
): { nextFocus: ZonedDate; stayInCurrentView: boolean } => {
    if (!currentFocus) {
        return { nextFocus: getPrimaryDateInView(currentView), stayInCurrentView: true };
    }

    const option = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, none: 0 };
    if (option[key] === undefined) {
        return { nextFocus: currentFocus, stayInCurrentView: true };
    }

    const nextFocus = clampDate(shiftByDays(currentFocus, option[key]), minDate, maxDate);

    return { nextFocus, stayInCurrentView: inSameMonth(nextFocus, currentFocus) };
};

export const setDomTabFocus = (
    wrapper: RefObject<HTMLDivElement>,
    removeFocusedDate: () => void,
    reverse?: boolean,
): void => {
    if (!wrapper || !wrapper.current) {
        return;
    }

    const currently: HTMLElement[] = [];
    wrapper.current.querySelectorAll('button[tabIndex="0"]').forEach((node) => {
        if (node.clientWidth) {
            currently.push(node as HTMLElement);
        }
    });

    if (!currently.length) {
        return;
    }

    const { activeElement } = document;

    let index = Array.from(currently).findIndex((button) => button === activeElement);

    index += reverse ? -1 : 1;
    if (index === -2 || (index === -1 && reverse)) {
        index = currently.length - 1;
    } else {
        index %= currently.length;
    }

    (currently[index] ?? currently[0]).focus();

    if (activeElement instanceof HTMLElement && activeElement.dataset.dateInFocus === 'true') {
        removeFocusedDate();
    }
};

export const isCalendarInFocus = (wrapper: { current: HTMLDivElement | null } | null): boolean => {
    if (!wrapper || !wrapper.current) {
        return false;
    }

    if (wrapper && wrapper.current === document.activeElement) {
        return true;
    }

    const btnList = wrapper.current.querySelectorAll('button');
    const activeBtn = Array.from(btnList).find((button) => button === document.activeElement);
    return !!activeBtn;
};
